// StoreBadges.tsx - badges « App Store » et « Google Play ».
//
// Promu dans le design system le 21 septembre 2026 : il vivait dans le widget
// seul, la landing en a désormais besoin aussi. Une seule implémentation, donc
// un seul endroit où remplacer les dessins par les fichiers officiels.
//
// Dessinés en **SVG intégré**, jamais chargés depuis un CDN : le widget
// s'interdit tout appel réseau vers un tiers (spec §6 - poids, et §1 - aucun
// traceur), et la landing s'interdit toute requête vers un domaine tiers.
// Ils reprennent la forme officielle - cartouche noir à coin arrondi, liseré
// clair, logo à gauche, « Télécharger dans / Disponible sur » en petit
// au-dessus du nom du store - et le libellé FRANÇAIS officiel de chaque store.
//
// ⚠️ Ce sont des REPRODUCTIONS, pas les fichiers d'Apple et de Google. Leurs
// chartes de marque imposent l'usage de leurs propres visuels : avant une
// diffusion large, télécharger les badges officiels (Apple Marketing
// Resources, générateur de badge Google Play) et les embarquer en `public/` -
// jamais en lien externe. Le reste du composant (tailles, accessibilité,
// cible) n'aura pas à bouger.
import type { ReactElement, ReactNode } from "react";

export interface StoreBadgeProps {
  /** Fiche du store (APP_STORE_URL / PLAY_STORE_URL de @hop-resto/shared). */
  href: string;
  /**
   * Hauteur du cartouche en pixels, bordure comprise. Tout le contenu suit
   * proportionnellement - le badge reste dessiné juste à n'importe quelle
   * échelle. Défaut 36 : la taille d'origine du widget.
   */
  hauteur?: number;
}

/** Hauteur de référence du dessin - base des proportions. */
const HAUTEUR_BASE = 36;

export function BadgeAppStore({ href, hauteur = HAUTEUR_BASE }: StoreBadgeProps): ReactElement {
  const k = hauteur / HAUTEUR_BASE;
  return (
    <Cartouche href={href} label="Télécharger dans l'App Store" k={k}>
      <svg
        width={18 * k}
        height={22 * k}
        viewBox="0 0 18 22"
        aria-hidden
        focusable="false"
        style={{ flexShrink: 0 }}
      >
        {/* pomme stylisée */}
        <path
          fill="#fff"
          d="M14.6 11.7c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.5 0-2.8.8-3.6 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.2 1.1 0 1.5-.7 2.8-.7 1.3 0 1.7.7 2.8.7 1.2 0 1.9-1 2.6-2.1.8-1.2 1.2-2.4 1.2-2.4-.1 0-2.2-.9-2.2-3.3z"
        />
        <path fill="#fff" d="M12.4 5.1c.6-.7 1-1.7.9-2.7-.9 0-2 .6-2.6 1.3-.6.6-1.1 1.7-.9 2.6 1 .1 2-.5 2.6-1.2z" />
      </svg>
      <Texte petit="Télécharger dans l'" grand="App Store" k={k} />
    </Cartouche>
  );
}

export function BadgeGooglePlay({ href, hauteur = HAUTEUR_BASE }: StoreBadgeProps): ReactElement {
  const k = hauteur / HAUTEUR_BASE;
  return (
    <Cartouche href={href} label="Disponible sur Google Play" k={k}>
      <svg
        width={19 * k}
        height={21 * k}
        viewBox="0 0 19 21"
        aria-hidden
        focusable="false"
        style={{ flexShrink: 0 }}
      >
        {/* le triangle Play, quatre facettes aux couleurs officielles */}
        <path fill="#00D0FF" d="M1 1.2c-.3.3-.5.8-.5 1.4v15.8c0 .6.2 1.1.5 1.4l.1.1 8.9-8.9v-.2L1 1.2z" />
        <path fill="#FFCE00" d="M13 13.9l-3-3v-.2l3-3 .1.1 3.5 2c1 .6 1 1.5 0 2.1l-3.5 2z" />
        <path fill="#FF3A44" d="M13.1 13.8L10 10.7 1 19.8c.3.4.9.4 1.5.1l10.6-6.1" />
        <path fill="#00F076" d="M13.1 7.6L2.5 1.5C1.9 1.2 1.3 1.2 1 1.6l9 9.1z" />
      </svg>
      <Texte petit="Disponible sur" grand="Google Play" k={k} />
    </Cartouche>
  );
}

// ── forme commune ───────────────────────────────────────────
function Cartouche({
  href,
  label,
  k,
  children,
}: {
  href: string;
  label: string;
  /** Facteur d'échelle (1 = taille d'origine). */
  k: number;
  children: ReactNode;
}): ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8 * k,
        background: "#000",
        border: "1px solid #A6A6A6",
        borderRadius: 8 * k,
        padding: `${5 * k}px ${10 * k}px`,
        textDecoration: "none",
        flexShrink: 0,
      }}
    >
      {children}
    </a>
  );
}

function Texte({ petit, grand, k }: { petit: string; grand: string; k: number }): ReactElement {
  return (
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        lineHeight: 1.1,
        color: "#fff",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: 8 * k, fontWeight: 500, letterSpacing: "0.02em" }}>{petit}</span>
      <span style={{ fontSize: 14 * k, fontWeight: 600, letterSpacing: "-0.01em" }}>{grand}</span>
    </span>
  );
}
